<script>
  import Chip from '@leaflink/stash/Chip.vue';
  import tooltip from '@leaflink/stash/directives/tooltip';
  import Icon from '@leaflink/stash/Icon.vue';
  import { storeToRefs } from 'pinia';

  import { PLACEHOLDER_IMAGE_URL } from '@/constants';
  import ToggleStoreFavorite from '@/pages/Shop/Purchasing/components/ToggleStoreFavorite';
  import { useShopStore } from '@/piniaStores/shop/shop.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';
  import { money } from '@/utils/i18n';

  import { hasBaseUnitPricing, hasSalePrice, hasVolumeDiscount, isParent } from '../utils';

  export default {
    name: 'VendorMenuProduct',

    components: {
      Chip,
      Icon,
      ToggleStoreFavorite,
    },

    directives: {
      tooltip,
    },

    props: {
      campaign: {
        type: Object,
        default: () => ({}),
      },

      product: {
        type: Object,
        required: true,
      },

      isPreview: Boolean,

      isPublicMenu: Boolean,

      isFavorited: {
        type: Boolean,
        default: false,
      },

      isUpdatingFavoriteStatus: {
        type: Boolean,
        default: false,
      },

      showFavoriteIcon: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['click', 'input', 'update:is-updating-favorite-status', 'update:is-favorited'],

    data() {
      return {
        PLACEHOLDER_IMAGE_URL,
      };
    },

    computed: {
      /**
       * Effective price range for all the buyers
       */
      effectivePriceRangeString() {
        const { children } = this.product;

        if (!children) {
          return;
        }

        const prices = children.map((i) => i.effectivePrice.amount);

        const priceRange = {
          max: {
            ...children[0].effectivePrice,
            amount: Math.max(...prices),
          },
          min: {
            ...children[0].effectivePrice,
            amount: Math.min(...prices),
          },
        };

        let priceString = money(priceRange.min);

        if (priceRange.max.amount !== priceRange.min.amount) {
          priceString += ` - ${money(priceRange.max)}`;
        }

        return priceString;
      },

      /**
       * Standalone or first variety
       */
      firstVariety() {
        const { product, isParent } = this;

        return isParent(product) ? product.children[0] : product;
      },

      /**
       * Checks if it should show placeholder image for preview menu
       */
      isPreviewImage() {
        return this.isPreview && !this.product.hasImage;
      },

      /**
       * returns product specs array
       */
      productSpecs() {
        const { productSpecs, strainClassification } = this.product;
        const strain =
          strainClassification && strainClassification !== 'N/A'
            ? [{ name: 'Strain', value: strainClassification }]
            : [];

        return [...strain, ...productSpecs];
      },
    },

    setup() {
      const shopStore = useShopStore();

      const { getIsCampaignLive } = storeToRefs(shopStore);

      const { user, userActiveCompany } = storeToRefs(useUserStore());

      return {
        activeCompany: userActiveCompany,
        getIsCampaignLive,
        user,
      };
    },

    methods: {
      t: i18n.t,
      isParent,
      hasBaseUnitPricing,
      hasSalePrice,
      hasVolumeDiscount,
      money,
    },
  };
</script>

<template>
  <div class="ll-grid md:ll-grid-cols-8 lg:ll-grid-cols-12">
    <!-- Image Area -->
    <div class="col-span-3 md:col-span-2 lg:col-span-3 relative" :class="$style.imageWrapper">
      <img
        loading="lazy"
        :alt="`${product.name} ${t('common.product')} ${t('common.image')}`"
        :class="[
          'w-full cursor-pointer',
          $style.image,
          isPreviewImage && `border border-red rounded ${$style.imagePreview}`,
        ]"
        :src="isPreviewImage ? PLACEHOLDER_IMAGE_URL : product.featuredImage"
        @click="$emit('click', firstVariety.id)"
      />
      <ToggleStoreFavorite
        v-if="showFavoriteIcon"
        :is-favorited="isFavorited"
        :is-loading="isUpdatingFavoriteStatus"
        :product="product"
        tracking-source="Shop Brands Product Image"
        variant="over-image"
        :variant-options="{ position: 'top-right' }"
        @before-toggle="() => $emit('update:is-updating-favorite-status', true)"
        @after-toggle="() => $emit('update:is-updating-favorite-status', false)"
        @success="() => $emit('update:is-favorited', !isFavorited)"
      />
    </div>

    <!-- Actions Area -->
    <slot name="actions"></slot>

    <!-- Description  Area -->
    <div class="col-span-10 md:col-span-3 lg:col-span-4">
      <a data-test="product-name" class="cursor-pointer" @click="$emit('click', firstVariety.id)">
        {{ product.displayName }}
        <span v-if="product.isBackordered && !isPublicMenu" class="color-red">({{ t('common.onBackorder') }})</span>
      </a>
      <Chip v-if="product.isNew" class="text-uppercase" shade="main" size="small" color-scheme="red">{{
        t('common.new')
      }}</Chip>

      <div class="flex align-justify">
        <slot name="pricing">
          <div v-if="!isPublicMenu" class="font-weight-medium ll-mt-1">
            <!-- Variety Pricing -->
            <span v-if="isParent(product)" class="color-ice-900 text-lowercase">
              {{ effectivePriceRangeString }}
            </span>
            <template v-else>
              <!-- Item Pricing -->
              <template v-if="hasSalePrice(product)">
                <span class="color-ice text-strike-through">
                  {{ money(product.basePrice) }}
                </span>
                <span class="color-ice-900 text-lowercase">
                  {{ money(product.salePrice) }}/{{ product.displayPriceUnit }}
                </span>
              </template>
              <span v-else class="color-ice-900 text-lowercase">
                {{ money(product.basePrice) }}/{{ product.displayPriceUnit }}
              </span>

              <div class="text-small text-lowercase ll-mt-1">
                <!-- Unit Pricing -->
                <span v-if="product.unitMultiplier > 1" class="ll-mr-1">
                  {{ money(product.pricePerUnit) }}/{{ t('common.unit') }}
                </span>

                <!-- Price Per Base Unit -->
                <span v-if="hasBaseUnitPricing(product)">
                  {{ money(product.baseUnitPrice) }}/{{ product.subCategory.baseUnitOfMeasureShortName }}
                </span>
              </div>
            </template>
          </div>
          <!-- Tag Area -->
          <div class="flex ll-mt-1">
            <div
              v-if="product.isFeaturedOnDeals && getIsCampaignLive"
              v-tooltip.top="t('shop.deals.featuredOnDeals', { campaignLabel: campaign.label })"
              class="ll-ml-1"
            >
              <Icon name="tag-star" class="color-teal" />
            </div>
            <div v-else-if="hasSalePrice(product)" v-tooltip.top="t('shop.saleDiscountTooltip')" class="ll-ml-1">
              <Icon name="tag" class="color-red" />
            </div>
            <div v-if="hasVolumeDiscount(product)" v-tooltip.top="t('shop.volumeDiscountTooltip')" class="ll-ml-1">
              <Icon name="tag" class="color-orange" />
            </div>
          </div>
        </slot>
      </div>
    </div>

    <!-- Right Input Area -->
    <slot name="input">
      <!-- Tagline area -->
      <p class="ll-mt-3 ll-mb-0 col-span-10 md:col-span-2 lg:col-span-4">
        {{ product.tagline }}
      </p>
    </slot>

    <!-- Product Details Area-->
    <div
      class="align-self-start text-small flex flex-wrap color-ice-900 col-span-10 md:order-2 md:col-span-6 lg:col-span-8 ll-mt-2"
    >
      <dl v-for="(data, i) in productSpecs" class="ll-mr-3 ll-mb-1" :key="i">
        <dt class="bold">
          <strong>{{ data.name }}</strong>
        </dt>
        <dd>{{ data.value }}</dd>
      </dl>
    </div>
  </div>
</template>

<style module lang="scss">
  .imageWrapper {
    align-items: center;
    display: flex;
    min-height: 132px;

    @include breakpoint('md') {
      grid-row-end: 3;
      grid-row-start: 1;
    }
  }

  .image {
    height: auto;
    max-width: 100%;
  }

  .imagePreview {
    object-fit: cover;
  }
</style>
